import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Pill,
  ShieldCheck
} from 'lucide-react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/shared/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useCreateMedifarmaRegistroPublico } from '@/modules/medifarma/hooks/useMedifarma'

// Logo de Medifarma
const MEDIFARMA_LOGO = 'https://pub-6088cbb5f5b14208850ccf04d9064328.r2.dev/logos/medifarma.png'

// Provincias de Ecuador
const PROVINCIAS_ECUADOR = [
  'Azuay',
  'Bolívar',
  'Cañar',
  'Carchi',
  'Chimborazo',
  'Cotopaxi',
  'El Oro',
  'Esmeraldas',
  'Galápagos',
  'Guayas',
  'Imbabura',
  'Loja',
  'Los Ríos',
  'Manabí',
  'Morona Santiago',
  'Napo',
  'Orellana',
  'Pastaza',
  'Pichincha',
  'Santa Elena',
  'Santo Domingo de los Tsáchilas',
  'Sucumbíos',
  'Tungurahua',
  'Zamora Chinchipe',
]

// Validaciones personalizadas
const registroSchema = z.object({
  nombre: z
    .string()
    .min(1, 'El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres'),

  correo: z
    .string()
    .min(1, 'El correo es requerido')
    .refine((email) => email.includes('@'), {
      message: 'El correo debe contener un @',
    })
    .refine((email) => {
      const parts = email.split('@')
      if (parts.length !== 2) return false
      const afterAt = parts[1]
      return afterAt.length >= 3
    }, {
      message: 'Debe haber al menos 3 letras después del @',
    })
    .refine((email) => {
      const parts = email.split('@')
      if (parts.length !== 2) return false
      const afterAt = parts[1]
      return afterAt.includes('.')
    }, {
      message: 'El correo debe tener un punto (.) después del @',
    }),

  telefono: z
    .string()
    .min(1, 'El teléfono es requerido')
    .refine((tel) => {
      const digits = tel.replace('+593', '')
      return /^\d+$/.test(digits)
    }, {
      message: 'El teléfono solo debe contener números',
    })
    .refine((tel) => {
      const digits = tel.replace('+593', '')
      return digits.length === 9
    }, {
      message: 'El teléfono debe tener exactamente 9 números después de +593',
    })
    .refine((tel) => {
      const digits = tel.replace('+593', '')
      return digits.startsWith('9')
    }, {
      message: 'El teléfono debe comenzar con 9',
    }),

  ciudad: z
    .string()
    .min(1, 'La ciudad/provincia es requerida'),

  farmacia: z
    .string()
    .min(1, 'El nombre de la farmacia es requerido')
    .min(3, 'El nombre de la farmacia debe tener al menos 3 caracteres')
    .max(200, 'El nombre de la farmacia no puede exceder 200 caracteres'),

  aceptaTerminos: z
    .boolean()
    .refine((val) => val === true, {
      message: 'Debe aceptar el Aviso de Privacidad',
    }),
})

type RegistroFormData = z.infer<typeof registroSchema>

// Mensajes de agradecimiento
const MENSAJE_GRACIAS = 'Agradecemos su interés.'
const MENSAJE_DETALLE = 'Su solicitud está siendo procesada, en las próximas horas nuestro equipo estará en comunicación con usted.'

export function RegistroPage() {
  const createMutation = useCreateMedifarmaRegistroPublico()
  const [isSuccess, setIsSuccess] = useState(false)
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [shakeCheckbox, setShakeCheckbox] = useState(false)

  const form = useForm<RegistroFormData>({
    resolver: zodResolver(registroSchema),
    mode: 'onBlur',
    defaultValues: {
      nombre: '',
      correo: '',
      telefono: '',
      ciudad: '',
      farmacia: '',
      aceptaTerminos: false,
    },
  })

  // Cambiar título de la página
  useEffect(() => {
    document.title = 'Medifarma - Solicitud'
  }, [])

  // Efecto para animación de sacudir del checkbox
  useEffect(() => {
    if (shakeCheckbox) {
      const timer = setTimeout(() => setShakeCheckbox(false), 300)
      return () => clearTimeout(timer)
    }
  }, [shakeCheckbox])

  const onSubmit = async (data: RegistroFormData) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { aceptaTerminos, ...submitData } = data
      await createMutation.mutateAsync(submitData)
      setIsSuccess(true)
      setIsEmailDuplicate(false)
      form.reset()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Error al enviar la solicitud'

      // Mensaje específico para correo duplicado
      if (errorMessage.includes('correo')) {
        setIsEmailDuplicate(true)
      }
    }
  }

  const handleSubmitWithValidation = (e: React.FormEvent) => {
    const aceptaTerminos = form.getValues('aceptaTerminos')
    if (!aceptaTerminos) {
      setShakeCheckbox(true)
    }
    form.handleSubmit(onSubmit)(e)
  }

  const handleNewRequest = () => {
    setIsSuccess(false)
    setIsEmailDuplicate(false)
    form.reset()
  }

  // Manejar input de teléfono
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    let value = e.target.value

    // Si el usuario intenta borrar todo, permitirlo
    if (value === '' || value === '+' || value === '+5' || value === '+59' || value === '+593') {
      onChange('')
      return
    }

    // Remover el prefijo para trabajar con los dígitos
    let digits = value.replace('+593', '').replace(/\D/g, '')

    // No permitir que empiece con 0
    if (digits.startsWith('0')) {
      digits = digits.substring(1)
    }

    // Si empieza con 9, agregar el prefijo automáticamente
    if (digits.startsWith('9') && digits.length > 0) {
      // Limitar a 9 dígitos
      digits = digits.substring(0, 9)
      onChange('+593' + digits)
    } else if (digits.length > 0) {
      // Si no empieza con 9, no permitir
      onChange('')
    } else {
      onChange('')
    }
  }

  // Pantalla de éxito
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-600 to-white">
        <div className="w-full max-w-md bg-white rounded-lg p-8 text-center shadow-2xl">
          {/* Logo */}
          <img
            src={MEDIFARMA_LOGO}
            alt="Medifarma"
            className="h-14 mx-auto mb-6"
          />
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-green-100">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">
            ¡Solicitud Enviada!
          </h2>
          <p className="text-base font-semibold mb-2 text-gray-700">
            {MENSAJE_GRACIAS}
          </p>
          <p className="text-sm mb-6 text-gray-600">
            {MENSAJE_DETALLE}
          </p>
          <Button
            onClick={handleNewRequest}
            className="px-6 py-2.5 font-semibold text-sm transition-all duration-200 rounded-full bg-blue-900 text-white hover:bg-white hover:text-blue-900 hover:ring-2 hover:ring-inset hover:ring-blue-900"
          >
            Enviar otra solicitud
          </Button>
        </div>
      </div>
    )
  }

  // Pantalla de correo duplicado
  if (isEmailDuplicate) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-600 to-white">
        <div className="w-full max-w-md bg-white rounded-lg p-8 text-center shadow-2xl">
          {/* Logo */}
          <img
            src={MEDIFARMA_LOGO}
            alt="Medifarma"
            className="h-14 mx-auto mb-6"
          />
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-pink-100">
            <AlertCircle className="w-10 h-10 text-pink-600" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">
            Correo ya Registrado
          </h2>
          <p className="text-base font-semibold mb-2 text-gray-700">
            {MENSAJE_GRACIAS}
          </p>
          <p className="text-sm mb-6 text-gray-600">
            {MENSAJE_DETALLE}
          </p>
          <Button
            onClick={handleNewRequest}
            className="px-6 py-2.5 font-semibold text-sm transition-all duration-200 rounded-full bg-blue-900 text-white hover:bg-white hover:text-blue-900 hover:ring-2 hover:ring-inset hover:ring-blue-900"
          >
            Enviar otra solicitud
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-pink-600 to-white">
      <div className="max-w-lg mx-auto">
        {/* Card blanca con logo, formulario y footer */}
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header con Logo */}
          <div className="text-center py-6">
            <img
              src={MEDIFARMA_LOGO}
              alt="Medifarma"
              className="h-16 md:h-20 mx-auto"
            />
          </div>

          {/* Header del formulario */}
          <div className="px-6 py-4 border-t border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Formulario de Solicitud
            </h2>
            <p className="text-sm mt-1 text-gray-500">
              Complete todos los campos para enviar su solicitud
            </p>
          </div>

          {/* Contenido del formulario */}
          <div className="p-6">
            <Form {...form}>
              <form onSubmit={handleSubmitWithValidation} className="space-y-4">
                {/* Nombre */}
                <FormField
                  control={form.control}
                  name="nombre"
                  render={({ field, fieldState }) => {
                    const isFocused = focusedField === 'nombre'
                    const hasError = fieldState.error && fieldState.isTouched && !isFocused
                    return (
                      <FormItem>
                        <FormLabel
                          className={`text-sm font-normal flex items-center gap-2 ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                        >
                          <User className={`w-4 h-4 ${hasError ? 'text-red-500' : 'text-blue-900'}`} />
                          Nombre Completo <span className="text-pink-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Ingrese su nombre completo"
                            className={`h-[33px] text-sm transition-all duration-200 rounded placeholder:text-blue-900/40 border-gray-300 dark:border-gray-700 focus:border-blue-900 focus-visible:ring-0 ${hasError ? 'border-red-500' : ''}`}
                            {...field}
                            onFocus={() => setFocusedField('nombre')}
                            onBlur={() => {
                              field.onBlur()
                              setFocusedField(null)
                            }}
                            disabled={createMutation.isPending}
                          />
                        </FormControl>
                        {isFocused && (
                          <p className="text-xs mt-1 text-gray-500">
                            Ejemplo: María García López
                          </p>
                        )}
                        {hasError && (
                          <p className="text-xs mt-1 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Correo */}
                <FormField
                  control={form.control}
                  name="correo"
                  render={({ field, fieldState }) => {
                    const isFocused = focusedField === 'correo'
                    const hasError = fieldState.error && fieldState.isTouched && !isFocused
                    return (
                      <FormItem>
                        <FormLabel
                          className={`text-sm font-normal flex items-center gap-2 ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                        >
                          <Mail className={`w-4 h-4 ${hasError ? 'text-red-500' : 'text-blue-900'}`} />
                          Correo Electrónico <span className="text-pink-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Ingrese su correo electrónico"
                            className={`h-[33px] text-sm transition-all duration-200 rounded placeholder:text-blue-900/40 border-gray-300 dark:border-gray-700 focus:border-blue-900 focus-visible:ring-0 ${hasError ? 'border-red-500' : ''}`}
                            {...field}
                            onFocus={() => setFocusedField('correo')}
                            onBlur={() => {
                              field.onBlur()
                              setFocusedField(null)
                            }}
                            disabled={createMutation.isPending}
                          />
                        </FormControl>
                        {isFocused && (
                          <p className="text-xs mt-1 text-gray-500">
                            Ejemplo: maria.garcia@gmail.com
                          </p>
                        )}
                        {hasError && (
                          <p className="text-xs mt-1 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Teléfono */}
                <FormField
                  control={form.control}
                  name="telefono"
                  render={({ field, fieldState }) => {
                    const isFocused = focusedField === 'telefono'
                    const hasError = fieldState.error && fieldState.isTouched && !isFocused
                    return (
                      <FormItem>
                        <FormLabel
                          className={`text-sm font-normal flex items-center gap-2 ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                        >
                          <Phone className={`w-4 h-4 ${hasError ? 'text-red-500' : 'text-blue-900'}`} />
                          Teléfono Celular <span className="text-pink-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="+593 9XXXXXXXX"
                            className={`h-[33px] text-sm transition-all duration-200 rounded placeholder:text-blue-900/40 border-gray-300 dark:border-gray-700 focus:border-blue-900 focus-visible:ring-0 ${hasError ? 'border-red-500' : ''}`}
                            value={field.value}
                            onChange={(e) => handlePhoneChange(e, field.onChange)}
                            onFocus={() => setFocusedField('telefono')}
                            onBlur={() => {
                              field.onBlur()
                              setFocusedField(null)
                            }}
                            disabled={createMutation.isPending}
                          />
                        </FormControl>
                        {isFocused && (
                          <p className="text-xs mt-1 text-gray-500">
                            Ingrese 9 seguido de 8 dígitos (ej: 987654321)
                          </p>
                        )}
                        {hasError && (
                          <p className="text-xs mt-1 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Provincia */}
                <FormField
                  control={form.control}
                  name="ciudad"
                  render={({ field, fieldState }) => {
                    const isFocused = focusedField === 'ciudad'
                    const hasError = fieldState.error && fieldState.isTouched && !isFocused
                    return (
                      <FormItem>
                        <FormLabel
                          className={`text-sm font-normal flex items-center gap-2 ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                        >
                          <MapPin className={`w-4 h-4 ${hasError ? 'text-red-500' : 'text-blue-900'}`} />
                          Provincia <span className="text-pink-600">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value)
                            setFocusedField(null)
                          }}
                          defaultValue={field.value}
                          disabled={createMutation.isPending}
                          onOpenChange={(open) => {
                            if (open) {
                              setFocusedField('ciudad')
                            } else {
                              setFocusedField(null)
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={`h-[33px] text-sm rounded data-[placeholder]:text-blue-900 border-gray-300 dark:border-gray-700 focus:border-blue-900 focus:ring-0 data-[state=open]:border-blue-900 ${hasError ? 'border-red-500' : ''}`}
                            >
                              <SelectValue placeholder="Seleccione una provincia" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="rounded" position="popper" sideOffset={4}>
                            {PROVINCIAS_ECUADOR.map((provincia) => (
                              <SelectItem key={provincia} value={provincia} className="text-sm">
                                {provincia}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isFocused && (
                          <p className="text-xs mt-1 text-gray-500">
                            Ejemplo: Pichincha, Guayas, Azuay
                          </p>
                        )}
                        {hasError && (
                          <p className="text-xs mt-1 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Farmacia */}
                <FormField
                  control={form.control}
                  name="farmacia"
                  render={({ field, fieldState }) => {
                    const isFocused = focusedField === 'farmacia'
                    const hasError = fieldState.error && fieldState.isTouched && !isFocused
                    return (
                      <FormItem>
                        <FormLabel
                          className={`text-sm font-normal flex items-center gap-2 ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                        >
                          <Building2 className={`w-4 h-4 ${hasError ? 'text-red-500' : 'text-blue-900'}`} />
                          Nombre de la Farmacia <span className="text-pink-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Ingrese el nombre de su farmacia"
                            className={`h-[33px] text-sm transition-all duration-200 rounded placeholder:text-blue-900/40 border-gray-300 dark:border-gray-700 focus:border-blue-900 focus-visible:ring-0 ${hasError ? 'border-red-500' : ''}`}
                            {...field}
                            onFocus={() => setFocusedField('farmacia')}
                            onBlur={() => {
                              field.onBlur()
                              setFocusedField(null)
                            }}
                            disabled={createMutation.isPending}
                          />
                        </FormControl>
                        {isFocused && (
                          <p className="text-xs mt-1 text-gray-500">
                            Ejemplo: Cruz Azul, Fybeca, Medicity
                          </p>
                        )}
                        {hasError && (
                          <p className="text-xs mt-1 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Aceptar Términos */}
                <FormField
                  control={form.control}
                  name="aceptaTerminos"
                  render={({ field, fieldState }) => {
                    const hasError = fieldState.error && fieldState.isTouched
                    return (
                      <FormItem className="pt-2">
                        <div
                          className={`flex items-center gap-2 ${shakeCheckbox ? 'animate-shake' : ''}`}
                          style={shakeCheckbox ? { animation: 'shake 0.3s ease-in-out' } : {}}
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={createMutation.isPending}
                              className="data-[state=checked]:bg-blue-900 data-[state=checked]:border-blue-900"
                            />
                          </FormControl>
                          <FormLabel
                            className={`text-sm font-normal cursor-pointer ${hasError ? 'text-red-500' : 'text-gray-800'}`}
                          >
                            He leído y acepto el{' '}
                            <a
                              href="https://www.medifarmaconecta.com/politica-de-datos-personales"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`underline hover:no-underline ${hasError ? 'text-red-500' : 'text-blue-900'}`}
                            >
                              Aviso de Privacidad
                            </a>
                          </FormLabel>
                        </div>
                        {hasError && (
                          <p className="text-xs mt-1 ml-6 text-red-500">
                            {fieldState.error?.message}
                          </p>
                        )}
                      </FormItem>
                    )
                  }}
                />

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="w-full h-10 text-sm font-semibold transition-all duration-200 rounded-full bg-blue-900 text-white hover:bg-white hover:text-blue-900 hover:ring-2 hover:ring-inset hover:ring-blue-900"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar Solicitud'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Footer dentro de la tarjeta */}
          <div className="flex items-center justify-center gap-3 py-4 px-4 border-t border-gray-200">
            <div className="flex items-center gap-1.5">
              <Pill className="w-4 h-4 text-gray-500" />
              <p className="text-xs text-gray-500">
                Medifarma Conecta
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-gray-500" />
              <p className="text-xs text-gray-500">
                Powered by <span className="font-bold">Tonal Strategies</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
